import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="bg-black text-white min-h-screen py-20">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-white">Privacy Policy</CardTitle>
            <p className="text-zinc-400 text-sm mt-2">Last Updated: December 17, 2025</p>
          </CardHeader>
          <CardContent className="space-y-6 text-zinc-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Introduction</h2>
              <p>
                Sauce Sity Barbershop ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Information We Collect</h2>
              <p className="mb-2">We collect information that you provide directly to us, including:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Name and contact information (email, phone number)</li>
                <li>Appointment booking details and preferences</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Membership and loyalty program information</li>
                <li>Communication preferences</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">SMS/Text Message Policy</h2>
              <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-4">
                <p className="font-semibold text-lime-400 mb-2">Important Notice:</p>
                <p className="mb-3">
                  <strong>No mobile information will be shared with third parties for marketing or promotional purposes.</strong>
                </p>
                <p className="mb-2">
                  By opting in to receive SMS notifications, you consent to receive appointment reminders, confirmations, and service-related messages from Sauce Sity Barbershop.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                  <li>Your phone number will only be used for appointment-related communications</li>
                  <li>We do not sell, rent, or share your mobile information with third parties for marketing purposes</li>
                  <li>You can opt out at any time by replying STOP to any message</li>
                  <li>Standard message and data rates may apply</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">How We Use Your Information</h2>
              <p className="mb-2">We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Process and manage your appointments</li>
                <li>Send appointment confirmations and reminders</li>
                <li>Process payments securely</li>
                <li>Manage your loyalty rewards and membership benefits</li>
                <li>Communicate with you about our services</li>
                <li>Improve our services and customer experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Information Sharing and Disclosure</h2>
              <p className="mb-2">We do not sell your personal information. We may share your information only in the following circumstances:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Service Providers:</strong> We use trusted third-party services (Stripe for payments, Vonage for SMS) who are contractually obligated to protect your information</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Payment information is processed securely through Stripe and is never stored on our servers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Your Rights and Choices</h2>
              <p className="mb-2">You have the right to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Access, update, or delete your personal information</li>
                <li>Opt out of SMS notifications at any time by replying STOP</li>
                <li>Unsubscribe from promotional emails</li>
                <li>Request a copy of your data</li>
                <li>Object to certain processing of your information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Cookies and Tracking</h2>
              <p>
                We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookies through your browser settings. Note that disabling cookies may affect website functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Contact Us</h2>
              <p className="mb-2">If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
              <div className="bg-zinc-800 rounded-lg p-4 mt-3">
                <p className="font-semibold text-white">Sauce Sity Barbershop</p>
                <p>Phone: (239) 201-8105</p>
                <p>Email: info@saucesity.com</p>
                <p>Address: Cape Coral, FL</p>
              </div>
            </section>

            <section className="border-t border-zinc-700 pt-6">
              <p className="text-sm text-zinc-400">
                By using our services, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
